import React, { useState } from 'react';
import { ApiClient } from '../../api/client';
import {
  Copy, Check, Info, Lightbulb, AlertTriangle, AlertCircle,
  ExternalLink, Maximize2
} from 'lucide-react';

interface MarkdownRendererProps {
  content: string;
  className?: string;
  onImageClick?: (url: string, alt: string) => void;
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  className = '',
  onImageClick,
}) => {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const handleCopyCode = (code: string, idx: number) => {
    navigator.clipboard.writeText(code);
    setCopiedIndex(idx);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  if (!content) return null;

  // Split content into blocks
  const lines = content.replace(/\r\n/g, '\n').split('\n');
  const elements: React.ReactNode[] = [];

  let i = 0;
  let codeBlockCounter = 0;

  // Inline formatting helper
  const renderInline = (text: string): React.ReactNode => {
    if (!text) return null;

    // Pattern for inline tokens: images, links, bold, italic, strikethrough, inline code
    const tokens: React.ReactNode[] = [];
    let remaining = text;
    let keyIdx = 0;

    while (remaining.length > 0) {
      // 1. Image: ![alt](url)
      const imgMatch = remaining.match(/^!\[([^\]]*)\]\(([^)]+)\)/);
      if (imgMatch) {
        const alt = imgMatch[1];
        const rawUrl = imgMatch[2];
        const url = rawUrl.startsWith('/api/') ? ApiClient.getAuthFileUrl(rawUrl, false) : rawUrl;
        tokens.push(
          <span key={keyIdx++} className="inline-block my-2 max-w-full">
            <span
              onClick={() => onImageClick?.(url, alt)}
              className="group relative inline-block cursor-pointer overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 shadow-xs hover:border-indigo-400 dark:hover:border-indigo-600 transition"
            >
              <img
                src={url}
                alt={alt}
                className="max-h-80 w-auto rounded-xl object-contain group-hover:scale-101 transition duration-200"
                loading="lazy"
              />
              <span className="absolute bottom-1.5 right-1.5 p-1 bg-slate-900/70 text-white rounded-lg opacity-0 group-hover:opacity-100 transition shadow-xs">
                <Maximize2 className="w-3.5 h-3.5" />
              </span>
            </span>
            {alt && <span className="block text-[11px] text-slate-500 italic mt-0.5 text-center">{alt}</span>}
          </span>
        );
        remaining = remaining.slice(imgMatch[0].length);
        continue;
      }

      // 2. Link: [text](url)
      const linkMatch = remaining.match(/^\[([^\]]+)\]\(([^)]+)\)/);
      if (linkMatch) {
        const linkText = linkMatch[1];
        const rawUrl = linkMatch[2];
        const url = rawUrl.startsWith('/api/') ? ApiClient.getAuthFileUrl(rawUrl, true) : rawUrl;
        tokens.push(
          <a
            key={keyIdx++}
            href={url}
            target="_blank"
            rel="noreferrer"
            className="text-indigo-600 dark:text-cyan-400 font-semibold hover:underline inline-flex items-center gap-0.5"
          >
            <span>{linkText}</span>
            <ExternalLink className="w-3 h-3 opacity-70" />
          </a>
        );
        remaining = remaining.slice(linkMatch[0].length);
        continue;
      }

      // 3. Inline code: `code`
      const codeMatch = remaining.match(/^`([^`]+)`/);
      if (codeMatch) {
        tokens.push(
          <code
            key={keyIdx++}
            className="px-1.5 py-0.5 rounded-md font-mono text-[11px] font-medium bg-slate-100 dark:bg-slate-800 text-indigo-700 dark:text-indigo-300 border border-slate-200/80 dark:border-slate-700/80"
          >
            {codeMatch[1]}
          </code>
        );
        remaining = remaining.slice(codeMatch[0].length);
        continue;
      }

      // 4. Bold: **text** or __text__
      const boldMatch = remaining.match(/^(\*\*|__)(.*?)\1/);
      if (boldMatch) {
        tokens.push(
          <strong key={keyIdx++} className="font-bold text-slate-900 dark:text-white">
            {renderInline(boldMatch[2])}
          </strong>
        );
        remaining = remaining.slice(boldMatch[0].length);
        continue;
      }

      // 5. Italic: *text* or _text_
      const italicMatch = remaining.match(/^(\*|_)(.*?)\1/);
      if (italicMatch) {
        tokens.push(
          <em key={keyIdx++} className="italic text-slate-800 dark:text-slate-200">
            {renderInline(italicMatch[2])}
          </em>
        );
        remaining = remaining.slice(italicMatch[0].length);
        continue;
      }

      // 6. Strikethrough: ~~text~~
      const strikeMatch = remaining.match(/^~~(.*?)~~/);
      if (strikeMatch) {
        tokens.push(
          <del key={keyIdx++} className="line-through text-slate-400">
            {renderInline(strikeMatch[1])}
          </del>
        );
        remaining = remaining.slice(strikeMatch[0].length);
        continue;
      }

      // Plain character or text chunk until next markdown symbol
      const nextSpecial = remaining.search(/[!\[`*_~]/);
      if (nextSpecial === -1) {
        tokens.push(remaining);
        remaining = '';
      } else if (nextSpecial === 0) {
        // Just the special char itself if no match
        tokens.push(remaining[0]);
        remaining = remaining.slice(1);
      } else {
        tokens.push(remaining.slice(0, nextSpecial));
        remaining = remaining.slice(nextSpecial);
      }
    }

    return tokens;
  };

  while (i < lines.length) {
    const line = lines[i];

    // Fenced Code block (```lang ... ```)
    if (line.trim().startsWith('```')) {
      const lang = line.trim().replace(/^```/, '').trim() || 'text';
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // skip closing ```

      const codeString = codeLines.join('\n');
      const curIdx = codeBlockCounter++;

      elements.push(
        <div key={`code-${i}`} className="my-3 rounded-xl overflow-hidden border border-slate-700/80 bg-slate-950 text-slate-100 shadow-md">
          <div className="flex items-center justify-between px-3.5 py-1.5 bg-slate-900 border-b border-slate-800 text-[11px] font-mono text-slate-400 select-none">
            <span className="uppercase tracking-wider">{lang}</span>
            <button
              type="button"
              onClick={() => handleCopyCode(codeString, curIdx)}
              className="flex items-center gap-1 hover:text-white transition cursor-pointer"
            >
              {copiedIndex === curIdx ? (
                <>
                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-emerald-400 text-[10px]">Másolva!</span>
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5" />
                  <span className="text-[10px]">Másolás</span>
                </>
              )}
            </button>
          </div>
          <pre className="p-3.5 text-xs font-mono overflow-x-auto leading-relaxed scrollbar-thin">
            <code>{codeString}</code>
          </pre>
        </div>
      );
      continue;
    }

    // Callout block: > [!NOTE], > [!TIP], > [!WARNING], > [!IMPORTANT]
    const calloutMatch = line.match(/^>\s*\[!(NOTE|TIP|WARNING|IMPORTANT|CAUTION)\]/i);
    if (calloutMatch) {
      const type = calloutMatch[1].toUpperCase();
      const calloutLines: string[] = [];
      i++;
      while (i < lines.length && lines[i].startsWith('>')) {
        calloutLines.push(lines[i].replace(/^>\s?/, ''));
        i++;
      }

      let badgeBg = 'bg-blue-50/70 border-blue-200 text-blue-900 dark:bg-blue-950/40 dark:border-blue-800/80 dark:text-blue-200';
      let icon = <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />;
      let title = 'Megjegyzés';

      if (type === 'TIP') {
        badgeBg = 'bg-emerald-50/70 border-emerald-200 text-emerald-900 dark:bg-emerald-950/40 dark:border-emerald-800/80 dark:text-emerald-200';
        icon = <Lightbulb className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />;
        title = 'Tipp & Javaslat';
      } else if (type === 'WARNING' || type === 'CAUTION') {
        badgeBg = 'bg-amber-50/70 border-amber-200 text-amber-900 dark:bg-amber-950/40 dark:border-amber-800/80 dark:text-amber-200';
        icon = <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />;
        title = 'Figyelmeztetés';
      } else if (type === 'IMPORTANT') {
        badgeBg = 'bg-purple-50/70 border-purple-200 text-purple-900 dark:bg-purple-950/40 dark:border-purple-800/80 dark:text-purple-200';
        icon = <AlertCircle className="w-4 h-4 text-purple-600 dark:text-purple-400 shrink-0 mt-0.5" />;
        title = 'Fontos Információ';
      }

      elements.push(
        <div key={`callout-${i}`} className={`my-3 p-3.5 rounded-xl border flex items-start gap-3 shadow-2xs ${badgeBg}`}>
          {icon}
          <div className="flex-1 min-w-0 space-y-1 text-xs">
            <span className="font-bold tracking-wide block uppercase text-[11px]">{title}</span>
            <div className="leading-relaxed whitespace-pre-wrap">
              {renderInline(calloutLines.join('\n'))}
            </div>
          </div>
        </div>
      );
      continue;
    }

    // Generic Blockquote: > text
    if (line.startsWith('>')) {
      const quoteLines: string[] = [line.replace(/^>\s?/, '')];
      i++;
      while (i < lines.length && lines[i].startsWith('>')) {
        quoteLines.push(lines[i].replace(/^>\s?/, ''));
        i++;
      }
      elements.push(
        <blockquote key={`quote-${i}`} className="my-2.5 pl-3.5 py-1 border-l-3 border-indigo-500 text-slate-600 dark:text-slate-300 italic text-xs leading-relaxed bg-slate-50/40 dark:bg-slate-900/30 rounded-r-lg">
          {renderInline(quoteLines.join('\n'))}
        </blockquote>
      );
      continue;
    }

    // Markdown Table: | Col 1 | Col 2 |
    if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
      const tableRows: string[] = [line];
      i++;
      while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
        tableRows.push(lines[i]);
        i++;
      }

      if (tableRows.length >= 2) {
        const headerRow = tableRows[0].split('|').slice(1, -1).map(c => c.trim());
        const isSeparator = (r: string) => r.includes('---');
        const dataRows = tableRows.slice(1).filter(r => !isSeparator(r)).map(r => r.split('|').slice(1, -1).map(c => c.trim()));

        elements.push(
          <div key={`table-${i}`} className="my-3 overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 shadow-2xs">
            <table className="w-full text-left text-xs border-collapse">
              <thead className="bg-slate-100 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 font-bold uppercase tracking-wider text-[11px]">
                <tr>
                  {headerRow.map((h, hIdx) => (
                    <th key={hIdx} className="px-3.5 py-2.5 whitespace-nowrap">
                      {renderInline(h)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {dataRows.map((row, rIdx) => (
                  <tr key={rIdx} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition">
                    {row.map((cell, cIdx) => (
                      <td key={cIdx} className="px-3.5 py-2 text-slate-800 dark:text-slate-300">
                        {renderInline(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        continue;
      }
    }

    // Headings: #, ##, ###, ####
    if (line.startsWith('#### ')) {
      elements.push(
        <h5 key={`h5-${i}`} className="text-xs font-bold text-slate-800 dark:text-slate-200 mt-3 mb-1 tracking-wide">
          {renderInline(line.replace(/^####\s+/, ''))}
        </h5>
      );
      i++;
      continue;
    }
    if (line.startsWith('### ')) {
      elements.push(
        <h4 key={`h4-${i}`} className="text-sm font-bold text-indigo-900 dark:text-cyan-300 mt-3.5 mb-1.5 flex items-center gap-1.5">
          <span className="w-1 h-3.5 rounded-full bg-indigo-600 dark:bg-cyan-400 inline-block"></span>
          <span>{renderInline(line.replace(/^###\s+/, ''))}</span>
        </h4>
      );
      i++;
      continue;
    }
    if (line.startsWith('## ')) {
      elements.push(
        <h3 key={`h3-${i}`} className="text-base font-extrabold text-slate-900 dark:text-white mt-4 mb-2 pb-1 border-b border-slate-200 dark:border-slate-800">
          {renderInline(line.replace(/^##\s+/, ''))}
        </h3>
      );
      i++;
      continue;
    }
    if (line.startsWith('# ')) {
      elements.push(
        <h2 key={`h2-${i}`} className="text-lg font-black text-slate-900 dark:text-white mt-4 mb-2.5 pb-1.5 border-b-2 border-indigo-500/40">
          {renderInline(line.replace(/^#\s+/, ''))}
        </h2>
      );
      i++;
      continue;
    }

    // Horizontal Rule: --- or ***
    if (/^(\*\*\*|---|___)$/.test(line.trim())) {
      elements.push(<hr key={`hr-${i}`} className="my-4 border-slate-200 dark:border-slate-800" />);
      i++;
      continue;
    }

    // Unordered List (- item or * item)
    if (/^[-*]\s+/.test(line)) {
      const listItems: string[] = [line.replace(/^[-*]\s+/, '')];
      i++;
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
        listItems.push(lines[i].replace(/^[-*]\s+/, ''));
        i++;
      }
      elements.push(
        <ul key={`ul-${i}`} className="my-2 space-y-1 pl-1 text-xs">
          {listItems.map((item, idx) => (
            <li key={idx} className="flex items-start gap-2 text-slate-800 dark:text-slate-200">
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-600 dark:bg-cyan-400 shrink-0 mt-1.5"></span>
              <span className="flex-1 leading-relaxed">{renderInline(item)}</span>
            </li>
          ))}
        </ul>
      );
      continue;
    }

    // Ordered List (1. item)
    if (/^\d+\.\s+/.test(line)) {
      const listItems: string[] = [line.replace(/^\d+\.\s+/, '')];
      i++;
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        listItems.push(lines[i].replace(/^\d+\.\s+/, ''));
        i++;
      }
      elements.push(
        <ol key={`ol-${i}`} className="my-2 space-y-1.5 pl-1 text-xs counter-reset-list">
          {listItems.map((item, idx) => (
            <li key={idx} className="flex items-start gap-2 text-slate-800 dark:text-slate-200">
              <span className="font-mono font-bold text-[11px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 shrink-0">
                {idx + 1}.
              </span>
              <span className="flex-1 leading-relaxed">{renderInline(item)}</span>
            </li>
          ))}
        </ol>
      );
      continue;
    }

    // Empty line
    if (!line.trim()) {
      elements.push(<div key={`empty-${i}`} className="h-2" />);
      i++;
      continue;
    }

    // Paragraph
    elements.push(
      <p key={`p-${i}`} className="text-xs text-slate-800 dark:text-slate-200 leading-relaxed my-1">
        {renderInline(line)}
      </p>
    );
    i++;
  }

  return (
    <div className={`prose-compact space-y-1 ${className}`}>
      {elements}
    </div>
  );
};
